/**
 * Legacy URL Redirect Module
 *
 * Intercepts old Demandware/Salesforce Commerce Cloud product URLs and redirects
 * to the equivalent Shopify product page. Runs immediately on script parse —
 * no DOM access needed — so visitors never see a 404 flash.
 *
 * - Converts Demandware locale format (en_CA) to Shopify format (en-ca)
 * - Maps Demandware PIDs to Shopify product handles
 * - Unknown PIDs redirect to the locale homepage
 * - Debug mode: append ?debug_legacy_url_redirect to log without redirecting
 *
 * @module legacy-url-redirect
 */

import {
  PRODUCT_MAP,
  convertLocale,
  extractDemandwareParams,
  buildShopifyUrl,
} from './constants.js';

// Re-export for testing
export {
  DEMANDWARE_PATH_PATTERN,
  PRODUCT_MAP,
  convertLocale,
  extractDemandwareParams,
  buildShopifyUrl,
} from './constants.js';

const LOG_PREFIX = '[Legacy URL Redirect]';

export function init() {
  const debugMode = new URLSearchParams(window.location.search).has('debug_legacy_url_redirect');

  const parsed = extractDemandwareParams(window.location.pathname, window.location.search);

  if (!parsed) return;

  const locale = convertLocale(parsed.locale);
  const handle = PRODUCT_MAP[parsed.pid];

  if (debugMode) {
    console.info(`${LOG_PREFIX} Debug mode — parsed:`, {
      pid: parsed.pid,
      locale: parsed.locale,
      convertedLocale: locale,
      handle: handle || '(unknown — would redirect to homepage)',
      redirectUrl: handle ? buildShopifyUrl(locale, handle) : `/${locale}/`,
    });
    return;
  }

  if (!handle) {
    console.warn(`${LOG_PREFIX} Unknown product ID: ${parsed.pid} — redirecting to homepage`);
    window.location.replace(`/${locale}/`);
    return;
  }

  const redirectUrl = buildShopifyUrl(locale, handle);
  console.info(`${LOG_PREFIX} Redirecting:`, redirectUrl);
  window.location.replace(redirectUrl);
}

// Execute immediately — no DOM needed, redirect before page renders
init();
